import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { TooltipProvider } from "@/components/ui/tooltip"
import { CredentialCopyRow } from "@/components/CredentialCopyRow"
import { cn } from "@prescreen/lib/utils"

export type OAuthCredentialsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  clientId: string
  /** Omitted when no OAuth secret has been created yet */
  clientSecret?: string
  /** Shown when issuing a named integration secret */
  secretId?: string
  /** Shown once when an integration is created or its webhook secret is rotated */
  webhookSecret?: string
  confirmButtonClassName?: string
}

export function OAuthCredentialsDialog({
  open,
  onOpenChange,
  title,
  description,
  clientId,
  clientSecret,
  secretId,
  webhookSecret,
  confirmButtonClassName,
}: OAuthCredentialsDialogProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-green-800">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium leading-none">
              Client ID
            </Label>
            <CredentialCopyRow value={clientId} className="mt-1" />
          </div>

          {clientSecret ? (
            <div>
              <Label className="text-sm font-medium leading-none">
                Client Secret
              </Label>
              <CredentialCopyRow value={clientSecret} className="mt-1" />
            </div>
          ) : webhookSecret ? null : (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              No OAuth secret yet—use <strong>Add secret</strong> on this integration’s card to create
              credentials and set permissions.
            </div>
          )}

          {webhookSecret ? (
            <div>
              <Label className="text-sm font-medium leading-none">
                Webhook secret
              </Label>
              <CredentialCopyRow value={webhookSecret} className="mt-1" />
              <p className="mt-1 text-xs text-muted-foreground">
                Separate from the OAuth client secret. Use it to verify webhook deliveries.
              </p>
            </div>
          ) : null}

          {secretId ? (
            <div>
              <Label className="text-sm font-medium leading-none">
                Secret ID
              </Label>
              <CredentialCopyRow value={secretId} className="mt-1" />
              <p className="mt-1 text-xs text-muted-foreground">
                Use this ID when referring to this secret in support requests or automation.
              </p>
            </div>
          ) : null}

          {clientSecret || webhookSecret ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">
                Save these credentials securely. They will not be shown again.
              </p>
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                "bg-green-600 hover:bg-green-700",
                confirmButtonClassName,
              )}
            >
              I've Saved These Credentials
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  )
}
